///////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * LensRecommendFrame.jsx
 * 수술 전 검사값을 기반으로 ICL 렌즈 Vault 예측/추천을 실행하는 화면
 */
///////////////////////////////////////////////////////////////////////////////////////////////////////

import React, { useMemo, useState } from 'react';
import {
  FaInfoCircle as Info,
  FaExclamationTriangle as TriangleAlert
} from 'react-icons/fa';
import {
  LuRotateCcw as RotateCcw,
  LuSearch as Search,
  LuSparkles as Sparkles
} from 'react-icons/lu';

import '../css/LensRecommendFrame.css';
import HistoryLookupModal from './lensRecommend/HistoryLookupModal';
import { calculateCurrentAge } from './lensRecommend/ageUtils';
import {
  extractHistoryRows,
  historyDate,
  historyEye,
  historyToForm
} from './lensRecommend/historyHelpers';
import {
  compareSizes,
  fetchLensExamHistory,
  fetchSimilarCases,
  numeric,
  recommendLens,
  saveLensRecommendResult,
  sphericalEquivalent,
  suggestedOrderSphere
} from '../utils/lensRecommend';

///////////////////////////////////////////////////////////////////////////////////////////////////////
// 기본 입력값
///////////////////////////////////////////////////////////////////////////////////////////////////////

const SIMILAR_CASE_TOP_K = 5;

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createInitialHistoryQuery = () => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setMonth(startDate.getMonth() - 3);

  return {
    chartNo: '',
    startDt: formatDate(startDate),
    endDt: formatDate(endDate)
  };
};

const initialForm = {
  // 환자 기본 정보
  chart_no: '',
  eye: 'OD',
  gender: 'F',
  birth_date: '',
  age_years: '',
  orientation: '수평',

  // OPMR / 각막 검사값
  opmr_sphere: '',
  opmr_cylinder: '',
  opmr_axis: '',
  k1: '',
  k_axis: '',
  k2: '',

  // 생체계측 검사값
  acd_preop: '',
  wtw: '',
  ct: '',
  exp_se: '',

  // CASIA2 검사값 - orientation 값에 따라 수평/수직 payload 필드로 변환된다.
  casia_acw: '',
  casia_clr: '',
  casia_ata: '',
  casia_acd: '',

  // Vault 예측용 렌즈 계획 (현재 UI에서는 비노출)
  lens_size: 12.1,
  lens_sphere: '',
  lens_cylinder: ''
};

const REQUIRED_FIELDS = {
  common: [
    'chart_no',
    'birth_date',
    'opmr_sphere',
    'opmr_cylinder',
    'opmr_axis',
    'k1',
    'k2',
    'k_axis',
    'acd_preop',
    'wtw',
    'ct',
    'casia_acw',
    'casia_clr',
    'casia_ata',
    'casia_acd'
  ],
  recommend: []
};

const mergeWarnings = (...warningLists) => {
  return Array.from(new Set(warningLists.flat().filter(Boolean)));
};

const getErrorMessage = (error, fallbackMessage) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const getSessionUser = () => {
  const userSession = sessionStorage.getItem('user');
  return userSession ? JSON.parse(userSession) : null;
};

const isBlank = (value) => {
  return value === undefined || value === null || String(value).trim() === '';
};

///////////////////////////////////////////////////////////////////////////////////////////////////////

function LensRecommendFrame() {
  /* ------------------------ State ------------------------ */
  const [form, setForm] = useState(initialForm);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyQuery, setHistoryQuery] = useState(createInitialHistoryQuery);
  const [historyRows, setHistoryRows] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [loadedHistoryLabel, setLoadedHistoryLabel] = useState('');
  const [result, setResult] = useState(null);
  const [sizeCompare, setSizeCompare] = useState([]);
  const [similarCases, setSimilarCases] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* ------------------------ 계산 값 ------------------------ */
  // 렌더링마다 반복 계산되는 OPMR SE, 권장 Sphere, ACD 경고 기준을 메모이제이션한다.
  const computed = useMemo(() => {
    const age = Number.isFinite(Number(form.age_years)) ? Number(form.age_years) : null;
    const sph = numeric(form.opmr_sphere, -8);
    const cyl = numeric(form.opmr_cylinder, -1);
    const opmrSe = sphericalEquivalent(sph, cyl);
    const suggested = suggestedOrderSphere(age ?? 30, sph, cyl);
    const acd = numeric(form.acd_preop, 0);
    const lensDiff = numeric(form.lens_sphere, suggested.sphere) - suggested.sphere;
    return { age, opmrSe, suggested, acd, lensDiff };
  }, [form]);

  /* ------------------------ Handler ------------------------ */
  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setValidationErrors((current) => {
      if (!current[key]) return current;

      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const updateBirthDate = (value) => {
    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;

    const age = calculateCurrentAge(value);
    setForm((current) => ({
      ...current,
      birth_date: value,
      age_years: age === null ? '' : String(age)
    }));
    setValidationErrors((current) => {
      if (!current.birth_date) return current;

      const next = { ...current };
      delete next.birth_date;
      return next;
    });
  };

  const openHistoryModal = () => {
    const nextHistoryQuery = { ...historyQuery, chartNo: form.chart_no };
    setHistoryQuery(nextHistoryQuery);
    setHistoryModalOpen(true);

    if (form.chart_no.trim()) {
      runHistorySearch(nextHistoryQuery);
    } else {
      setHistoryRows([]);
      setSelectedHistory(null);
      setHistoryError('');
    }
  };

  const closeHistoryModal = () => {
    if (historyLoading) return;
    setHistoryModalOpen(false);
  };

  const setHistoryPeriod = (months) => {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setMonth(startDate.getMonth() - months);
    setHistoryQuery((current) => ({
      ...current,
      startDt: formatDate(startDate),
      endDt: formatDate(endDate)
    }));
  };

  const runHistorySearch = async (query = historyQuery) => {
    const chartNo = query.chartNo.trim();

    if (!chartNo) {
      setHistoryError('차트번호를 입력해주세요.');
      return;
    }

    if (!query.startDt || !query.endDt) {
      setHistoryError('조회 시작일과 종료일을 입력해주세요.');
      return;
    }

    if (query.startDt > query.endDt) {
      setHistoryError('시작일은 종료일보다 늦을 수 없습니다.');
      return;
    }

    const userData = getSessionUser();
    if (!userData?.hospitalId) {
      setHistoryError('병원 정보가 없어 검사 이력을 조회할 수 없습니다.');
      return;
    }

    setHistoryLoading(true);
    setHistoryError('');
    setHistoryRows([]);
    setSelectedHistory(null);

    try {
      const response = await fetchLensExamHistory({
        chartNo,
        hospitalId: userData.hospitalId,
        startDt: query.startDt,
        endDt: query.endDt
      });
      setHistoryRows(extractHistoryRows(response));
    } catch (err) {
      setHistoryError(getErrorMessage(err, '검사 이력 조회에 실패했습니다.'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const applyHistoryToForm = () => {
    if (!selectedHistory) return;

    const nextForm = historyToForm(selectedHistory, form);
    const hasExistingInput = Object.entries(form).some(([key, value]) => value !== initialForm[key] && !isBlank(value));

    if (hasExistingInput && !window.confirm('현재 입력한 검사값이 선택한 이력 값으로 변경됩니다. 계속하시겠습니까?')) {
      return;
    }

    setForm(nextForm);
    setLoadedHistoryLabel(`${historyDate(selectedHistory)} · ${historyEye(selectedHistory)} 검사 이력에서 불러옴`);
    setValidationErrors({});
    resetResults();
    setHistoryModalOpen(false);
  };

  const validateForm = (actionType) => {
    const requiredFields = [
      ...REQUIRED_FIELDS.common,
      ...(REQUIRED_FIELDS[actionType] || [])
    ];

    const errors = {};

    requiredFields.forEach((fieldName) => {
      if (isBlank(form[fieldName])) {
        errors[fieldName] = '필수 입력값입니다.';
        return;
      }

      if (fieldName === 'birth_date') {
        if (calculateCurrentAge(form.birth_date) === null) {
          errors[fieldName] = '오늘 이전의 유효한 생년월일을 입력해주세요.';
        }
        return;
      }

      if (!Number.isFinite(Number(form[fieldName]))) {
        errors[fieldName] = '숫자만 입력해주세요.';
      }
    });

    return errors;
  };

  const validateBeforeRequest = (actionType) => {
    const errors = validateForm(actionType);
    const errorFields = Object.keys(errors);

    if (errorFields.length === 0) {
      setValidationErrors({});
      return true;
    }

    resetResults();
    setValidationErrors(errors);

    return false;
  };

  /* ------------------------ API Payload ------------------------ */
  // 화면 입력값을 ICL Vault API의 patient payload로 변환한다.
  const patient = () => {
    const base = {
      chart_no: form.chart_no || undefined,
      eye: form.eye,
      gender: form.gender,
      age_years: computed.age,
      orientation: form.orientation,
      sphere: numeric(form.opmr_sphere, -8),
      cylinder: numeric(form.opmr_cylinder, -1),
      axis: numeric(form.opmr_axis, 180),
      k1: numeric(form.k1, 43),
      k_axis: numeric(form.k_axis, 90),
      k2: numeric(form.k2, 44.5),
      k2_axis: numeric(form.k_axis, 90),
      acd_preop: numeric(form.acd_preop, 3.2),
      ct: numeric(form.ct, 0.53),
      wtw: numeric(form.wtw, 11.8),
      exp_se: numeric(form.exp_se, 0)
    };

    // API는 수평/수직 CASIA 값을 서로 다른 필드명으로 받으므로 선택 방향에 맞는 필드만 채운다.
    const casia = {
      casia_h_acw_mm: form.orientation === '수평' ? numeric(form.casia_acw, 11.7) : undefined,
      casia_h_clr_um: form.orientation === '수평' ? numeric(form.casia_clr, 50) : undefined,
      casia_h_ata_mm: form.orientation === '수평' ? numeric(form.casia_ata, 11.8) : undefined,
      casia_h_acd_mm: form.orientation === '수평' ? numeric(form.casia_acd, 3.2) : undefined,
      casia_v_acw_mm: form.orientation === '수직' ? numeric(form.casia_acw, 11.9) : undefined,
      casia_v_clr_um: form.orientation === '수직' ? numeric(form.casia_clr, 100) : undefined,
      casia_v_ata_mm: form.orientation === '수직' ? numeric(form.casia_ata, 12) : undefined,
      casia_v_acd_mm: form.orientation === '수직' ? numeric(form.casia_acd, 3.2) : undefined
    };

    return { ...base, ...casia };
  };

  const resetResults = () => {
    setResult(null);
    setSizeCompare([]);
    setSimilarCases([]);
    setWarnings([]);
    setError('');
  };

  const saveResultHistory = async ({
    actionType,
    patientPayload,
    lensPayload,
    resultData,
    compareData,
    similarData,
    warningData
  }) => {
    const userData = getSessionUser();

    if (!userData?.userNo) {
      throw new Error('로그인 사용자 정보가 없어 렌즈 추천 결과를 저장하지 못했습니다.');
    }

    await saveLensRecommendResult({
      userNo: userData.userNo,
      hospitalId: userData.hospitalId,
      actionType,
      form,
      patientPayload,
      lensPayload,
      result: resultData,
      sizeCompare: compareData,
      similarCases: similarData,
      warnings: warningData
    });
  };



  /* ------------------------ API Event ------------------------ */
  /*
   * Vault 예측 기능은 UI 개편으로 비노출 처리했습니다.
   * 추후 렌즈 계획 입력 화면을 복원할 때 아래 기능을 함께 복원합니다.
   */


  // 최적 렌즈 추천 결과 조회
  const runRecommend = async () => {
    if (!validateBeforeRequest('recommend')) return;

    setLoading(true);
    resetResults(); // 결과 초기화

    try {
      // 환자 정보를 조회
      const patientPayload = patient();

      // 최적 렌즈 추천 결과 조회
      const response = await recommendLens(patientPayload);

      // 추천 렌즈 정보를 설정
      const recommendedLens = {
        size_mm: response.result.recommended.size_mm,
        sphere: response.result.recommended.sphere,
        cylinder: numeric(form.opmr_cylinder, -1)
      };

      // 렌즈 size 비교 결과 조회
      const [compareResponse, similarResponse] = await Promise.all([
        compareSizes(patientPayload, recommendedLens), // 렌즈 size 비교 결과 조회
        fetchSimilarCases(patientPayload, SIMILAR_CASE_TOP_K) // 유사 케이스 조회
      ]);

      const mergedWarnings = mergeWarnings(response.warnings, compareResponse.warnings, similarResponse.warnings);

      setResult(response.result.recommended); // 추천 렌즈 정보 설정
      setSizeCompare(compareResponse.result); // 렌즈 size 비교 결과 설정
      setSimilarCases(similarResponse.result); // 유사 케이스 결과 설정
      setWarnings(mergedWarnings); // 경고 메시지 설정

      try {
        await saveResultHistory({
          actionType: 'recommend',
          patientPayload,
          lensPayload: recommendedLens,
          resultData: response.result.recommended,
          compareData: compareResponse.result,
          similarData: similarResponse.result,
          warningData: mergedWarnings
        });
      } catch (saveError) {
        console.error('[LensRecommendFrame] 추천 결과 저장 실패:', saveError);
      }

      // 추천 렌즈 정보를 설정
      setForm((current) => ({
        ...current,
        lens_size: response.result.recommended.size_mm,
        lens_sphere: String(response.result.recommended.sphere)
      }));
    } catch (err) {
      resetResults();
      setError(getErrorMessage(err, '추천 요청 실패'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setValidationErrors({});
    setLoadedHistoryLabel('');
    resetResults();
  };

  const axisLabel = form.orientation === '수직' ? 'V' : 'H';
  const showReport = loading || Boolean(error) || Boolean(result);


  /* ------------------------ 화면 렌더링 ------------------------ */
  return (
    <div className="lens-frame">
      <div className="lens-page">
        <header className="lens-header">
          <div>
            <h1 className="lens-title">ICL Vault AI</h1>
            <p className="lens-subtitle">검사값 기반 최적 렌즈 추천 및 기존 케이스 비교</p>
          </div>
        </header>

        <main className="lens-workspace">
          <div className="lens-input-column lens-panel">
            <PanelHeader title="검사값 입력" label="INPUT" />
            <div className="lens-panel-body">
            <Section title="환자 기본 정보">
              {loadedHistoryLabel ? <p className="lens-history-source">{loadedHistoryLabel}</p> : null}
              <div className="lens-grid">
                <div className="lens-chart-field">
                  <Field
                    label="차트번호"
                    value={form.chart_no}
                    onChangeText={(value) => update('chart_no', value)}
                    placeholder="조회 또는 직접 입력"
                    error={validationErrors.chart_no}
                  />
                  <button className="lens-history-open-button" type="button" onClick={openHistoryModal}>
                    <Search size={16} />
                    검사 이력 조회
                  </button>
                </div>
                <Segment label="성별" value={form.gender} options={['F', 'M']} onChange={(value) => update('gender', value)} />
                <Field
                  label="생년월일"
                  value={form.birth_date}
                  onChangeText={updateBirthDate}
                  type="date"
                  min="1900-01-01"
                  max={formatDate(new Date())}
                  error={validationErrors.birth_date}
                />
                <Field className="lens-age-field" label="나이" value={form.age_years} readOnly numeric />
                <Segment label="Eye" value={form.eye} options={['OD', 'OS']} onChange={(value) => update('eye', value)} />
                <Segment label="삽입 방향" value={form.orientation} options={['수평', '수직']} onChange={(value) => update('orientation', value)} />
              </div>
            </Section>

            <Section title="OPMR / 각막">
              <div className="lens-grid">
                <Field label="OPMR Sphere" value={form.opmr_sphere} onChangeText={(value) => update('opmr_sphere', value)} numeric error={validationErrors.opmr_sphere} />
                <Field label="OPMR Cylinder" value={form.opmr_cylinder} onChangeText={(value) => update('opmr_cylinder', value)} numeric error={validationErrors.opmr_cylinder} />
                <Field label="OPMR Axis" value={form.opmr_axis} onChangeText={(value) => update('opmr_axis', value)} numeric error={validationErrors.opmr_axis} />
                <Field label="K1" value={form.k1} onChangeText={(value) => update('k1', value)} numeric error={validationErrors.k1} />
                <Field label="K2" value={form.k2} onChangeText={(value) => update('k2', value)} numeric error={validationErrors.k2} />
                <Field label="K Axis" value={form.k_axis} onChangeText={(value) => update('k_axis', value)} numeric error={validationErrors.k_axis} />
              </div>
              <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>
                OPMR SE {computed.opmrSe.toFixed(2)}D · 권장 Sphere {computed.suggested.sphere.toFixed(2)}D · {computed.suggested.note}
                {Math.abs(computed.lensDiff) >= 0.5 ? ` · 현재 렌즈와 ${computed.lensDiff > 0 ? '+' : ''}${computed.lensDiff.toFixed(2)}D 차이` : ''}
              </Notice>
            </Section>

            <div className="lens-compact-pair">
              <Section title="생체계측">
                <div className="lens-grid">
                  <Field label="ACD" value={form.acd_preop} onChangeText={(value) => update('acd_preop', value)} numeric error={validationErrors.acd_preop} />
                  <Field label="WTW" value={form.wtw} onChangeText={(value) => update('wtw', value)} numeric error={validationErrors.wtw} />
                  <Field label="CT" value={form.ct} onChangeText={(value) => update('ct', value)} numeric error={validationErrors.ct} />
                  <Field label="Expected SE" value={form.exp_se} onChangeText={(value) => update('exp_se', value)} numeric error={validationErrors.exp_se} />
                </div>
                {computed.acd < 2.8 ? (
                  <Notice tone="warn" icon={<TriangleAlert size={15} color="#b45309" />}>
                    ACD {computed.acd.toFixed(2)}mm: 2.8mm 미만, CASIA2 ACD 대신 생체계측 ACD가 반영됩니다.
                  </Notice>
                ) : (
                  <Notice tone="info" icon={<Info size={15} color="#1d4ed8" />}>
                    예측 기준 ACD는 CASIA2 {axisLabel} ACD입니다.
                  </Notice>
                )}
              </Section>

              <Section title={`CASIA2 (${form.orientation})`}>
                <div className="lens-grid">
                  <Field label={`${axisLabel} ACW`} value={form.casia_acw} onChangeText={(value) => update('casia_acw', value)} numeric error={validationErrors.casia_acw} />
                  <Field label={`${axisLabel} CLR`} value={form.casia_clr} onChangeText={(value) => update('casia_clr', value)} numeric error={validationErrors.casia_clr} />
                  <Field label={`${axisLabel} ATA`} value={form.casia_ata} onChangeText={(value) => update('casia_ata', value)} numeric error={validationErrors.casia_ata} />
                  <Field label={`${axisLabel} ACD`} value={form.casia_acd} onChangeText={(value) => update('casia_acd', value)} numeric error={validationErrors.casia_acd} />
                </div>
              </Section>
            </div>

            <Section title="추천 실행">
              <div className="lens-recommend-action-panel">
                <div className="lens-recommend-action-copy">
                  <strong>검사값으로 최적 렌즈 추천</strong>
                  {/* <span>입력한 검사값으로 최적 렌즈를 추천합니다.</span> */}
                </div>
                <div className="lens-actions">
                  <ActionButton label="최적 추천 실행" icon={<Sparkles size={16} color="#ffffff" />} onClick={runRecommend} primary disabled={loading} />
                  <IconButton label="초기화" onClick={resetForm} />
                </div>
              </div>
            </Section>
            </div>
          </div>

          {showReport ? (
          <div className="lens-result-column lens-result-panel">
            <PanelHeader title="추천 리포트" label="RESULT" dark />
            <div className="lens-panel-body lens-result-panel-body">
              <section className="lens-result-section">
                <h2 className="lens-result-section-title">추천 결과</h2>
              {loading ? <div className="lens-loading" role="status" aria-label="로딩 중" /> : null}
              {error ? <Notice tone="bad" icon={<TriangleAlert size={15} color="#b91c1c" />}>{error}</Notice> : null}
              {result ? (
                <div className="lens-report-stack">
                  <RecommendResult result={result} />
                  {sizeCompare.length ? <SizeCompareTable rows={sizeCompare} /> : null}
                  {warnings.length ? (
                    <div className="lens-report-warnings">
                      {warnings.map((warning) => (
                        <Notice key={warning} tone="warn" icon={<TriangleAlert size={15} color="#b45309" />}>
                          {warning}
                        </Notice>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="lens-empty-text">검사값과 렌즈 계획을 입력한 뒤 예측 또는 추천을 실행하세요.</p>
              )}
              </section>
            </div>
          </div>
          ) : null}
        </main>

        {similarCases.length ? (
          <section className="lens-similar-result">
            <SimilarCasesTable rows={similarCases} />
          </section>
        ) : null}
      </div>
      {historyModalOpen ? (
        <HistoryLookupModal
          query={historyQuery}
          rows={historyRows}
          selectedRow={selectedHistory}
          loading={historyLoading}
          error={historyError}
          onClose={closeHistoryModal}
          onQueryChange={(key, value) => {
            setHistoryQuery((current) => ({ ...current, [key]: value }));
            setHistoryError('');
          }}
          onPeriodClick={setHistoryPeriod}
          onSearch={runHistorySearch}
          onSelect={setSelectedHistory}
          onApply={applyHistoryToForm}
        />
      ) : null}
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////
// 하위 UI 컴포넌트
///////////////////////////////////////////////////////////////////////////////////////////////////////

// 흰색 카드 영역 공통 레이아웃
function Section({ title, children }) {
  return (
    <section className="lens-section">
      <h2 className="lens-section-title">{title}</h2>
      {children}
    </section>
  );
}

function PanelHeader({ title, label, dark }) {
  return (
    <header className={`lens-panel-header${dark ? ' lens-panel-header-dark' : ''}`}>
      <h2 className={`lens-panel-title${dark ? ' lens-panel-title-dark' : ''}`}>{title}</h2>
      <span className={`lens-panel-label${dark ? ' lens-panel-label-dark' : ''}`}>{label}</span>
    </header>
  );
}

// 텍스트/숫자 입력 필드 공통 컴포넌트
function Field({
  className = '',
  label,
  value,
  onChangeText,
  placeholder,
  numeric: isNumeric,
  type = 'text',
  readOnly = false,
  min,
  max,
  error
}) {
  return (
    <label className={`lens-field ${className}`}>
      <span className="lens-label">{label}</span>
      <input
        className={`lens-input${error ? ' lens-input-error' : ''}`}
        type={type}
        value={value}
        onChange={(event) => onChangeText?.(event.target.value)}
        placeholder={placeholder}
        inputMode={isNumeric ? 'decimal' : 'text'}
        readOnly={readOnly}
        min={min}
        max={max}
      />
      {error ? <span className="lens-field-error">{error}</span> : null}
    </label>
  );
}

// OD/OS, 성별, 삽입 방향, 렌즈 size처럼 선택지가 고정된 값을 토글 버튼으로 표시한다.
function Segment({ label, value, options, onChange }) {
  return (
    <div className="lens-field">
      <span className="lens-label">{label}</span>
      <div className="lens-segment">
        {options.map((option) => {
          const active = option === value;
          return (
            <button
              className={`lens-segment-item${active ? ' lens-segment-active' : ''}`}
              key={option}
              type="button"
              onClick={() => onChange(option)}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// API 경고/안내/오류 메시지를 동일한 디자인으로 표시한다.
function Notice({ tone, icon, children }) {
  return (
    <div className={`lens-notice lens-notice-${tone}`}>
      {icon}
      <span className="lens-notice-text">{children}</span>
    </div>
  );
}

// 예측/추천 실행 버튼
function ActionButton({ label, icon, onClick, primary, disabled, type = 'button' }) {
  return (
    <button
      className={`lens-action-button ${primary ? 'lens-primary-button' : 'lens-secondary-button'}`}
      type={type}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      <span className={primary ? 'lens-primary-text' : ''}>{label}</span>
    </button>
  );
}

// 초기화처럼 아이콘만 필요한 보조 버튼
function IconButton({ label, onClick }) {
  return (
    <button className="lens-icon-button" type="button" aria-label={label} onClick={onClick}>
      <RotateCcw size={18} color="#334155" />
    </button>
  );
}

///////////////////////////////////////////////////////////////////////////////////////////////////////
// 결과 표시 컴포넌트
///////////////////////////////////////////////////////////////////////////////////////////////////////

// Vault 예측 API 결과 표시 컴포넌트 (현재 UI에서는 비노출).
// eslint-disable-next-line no-unused-vars
function PredictResult({ result }) {
  if (!result?.lens) {
    return <p className="lens-empty-text">예측 결과를 불러오는 중입니다.</p>;
  }

  return (
    <div>
      <div className="lens-metric-row">
        <Metric label="1일 vault" value={`${Math.round(result.predicted_vault_1day_um)} µm`} ok={result.in_range_1day_250_750} />
        <Metric label="1개월 vault" value={`${Math.round(result.predicted_vault_1month_um)} µm`} ok={result.in_range_1month_250_750} />
        <Metric label="목표" value={`${Math.round(result.target_vault_um)} µm`} />
      </div>
      <p className="lens-result-line">
        렌즈 {result.lens.size_mm}mm · Sphere {result.lens.sphere_d}D · Cylinder {result.lens.cylinder_d}D
      </p>
    </div>
  );
}

// 최적 렌즈 추천 API 결과를 표시한다.
function RecommendResult({ result }) {
  if (!result || result.size_mm === undefined) {
    return <p className="lens-empty-text">추천 결과를 불러오는 중입니다.</p>;
  }

  const oneMonthStatus = classifyVault(result.predicted_vault_1month_um);

  return (
    <div className="lens-recommend-result-block">
      <div className={`lens-decision-banner lens-decision-${oneMonthStatus.tone}`}>
        <div className="lens-decision-top">
          <div>
            <span className="lens-decision-label">최종 추천 렌즈</span>
            <strong className="lens-decision-value">{result.size_mm}mm</strong>
            <span className="lens-decision-sphere">Sphere {result.sphere}D</span>
          </div>
          <span className={`lens-decision-badge lens-badge-${oneMonthStatus.tone}`}>{oneMonthStatus.label}</span>
        </div>
        <p className="lens-decision-text">{oneMonthStatus.description}</p>
      </div>
      <div className="lens-metric-row">
        <Metric label="1개월 vault" value={`${Math.round(result.predicted_vault_1month_um)} µm`} status={oneMonthStatus.label} tone={oneMonthStatus.tone} />
        <Metric label="목표 차이" value={`${Math.round(result.dist_to_target_um)} µm`} />
        <Metric label="1일 vault" value={`${Math.round(result.predicted_vault_1day_um)} µm`} />
      </div>
    </div>
  );
}

// 추천 size 후보별 예측치를 순위로 표시한다.
function SizeCompareTable({ rows }) {
  return (
    <div className="lens-table-block">
      <div className="lens-table-title-row">
        <h3 className="lens-table-title lens-table-title-dark">Size별 비교 순위</h3>
        <span className="lens-table-hint lens-table-hint-dark">1개월 vault 500µm에 가까운 순서</span>
      </div>
      {rows.map((row) => {
        const status = classifyVault(row.predicted_vault_1month_um);
        return (
        <div className={`lens-table-row lens-table-row-${status.tone}${row.rank === 1 ? ' lens-table-row-best' : ''}`} key={row.size_mm}>
          <div className="lens-compare-rank-block">
            <span className="lens-rank">{row.rank}</span>
            {row.rank === 1 ? <span className="lens-best-badge">추천</span> : null}
          </div>
          <div className="lens-compare-main">
            <strong>{row.size_mm}mm</strong>
            <span className={`lens-vault-badge lens-badge-${status.tone}`}>{status.label}</span>
          </div>
          <CompareMetric label="1개월" value={`${Math.round(row.predicted_vault_1month_um)}µm`} />
          <CompareMetric label="목표차" value={`${Math.round(row.target_diff_um)}µm`} />
          <CompareMetric label="1일" value={`${Math.round(row.predicted_vault_1day_um)}µm`} subdued />
        </div>
        );
      })}
    </div>
  );
}

// 기존 수술 케이스 중 현재 입력값과 유사한 케이스를 표시한다.
function SimilarCasesTable({ rows }) {
  return (
    <div className="lens-table-block">
      <div className="lens-table-title-row">
        <h3 className="lens-table-title">유사 기존 케이스 순위</h3>
        <span className="lens-table-hint">비슷한 검사값의 실제 결과</span>
      </div>
      <div className="lens-case-grid">
        {rows.map((row) => (
          <div className={`lens-case-row${row.순위 === 1 ? ' lens-case-row-best' : ''}`} key={`${row.순위}-${row.유사도}-${row.ACD}`}>
            <div className="lens-case-head">
              <span className="lens-rank">{row.순위}</span>
              <div className="lens-case-head-copy">
                <span className="lens-case-title">유사도 {row.유사도}%</span>
                <span className="lens-similarity-track">
                  <span className={`lens-similarity-fill${row.순위 === 1 ? '' : ' lens-similarity-fill-muted'}`} style={{ width: `${Math.max(0, Math.min(100, Number(row.유사도)))}%` }} />
                </span>
              </div>
            </div>
            <div className="lens-case-detail-grid">
              <CaseDatum label="ACD" value={row.ACD} />
              <CaseDatum label="WTW" value={row.WTW} />
              <CaseDatum label="K1/K2" value={row['K1/K2']} />
              <CaseDatum label="Sphere" value={row.Sphere} />
              <CaseDatum label="렌즈" value={`${row['렌즈 size']}mm / ${row['렌즈 Sph']}D`} strong />
              <CaseDatum label="실측 1일" value={`${row['실측 1일 vault']}µm`} tone={classifyVault(parseVaultValue(row['실측 1일 vault'])).tone} />
              <CaseDatum label="실측 1개월" value={`${row['실측 1개월 vault']}µm`} tone={classifyVault(parseVaultValue(row['실측 1개월 vault'])).tone} strong />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompareMetric({ label, value, subdued }) {
  return (
    <div className="lens-compare-metric">
      <span>{label}</span>
      <strong className={subdued ? 'lens-compare-metric-subdued' : ''}>{value}</strong>
    </div>
  );
}

function CaseDatum({ label, value, strong, tone }) {
  return (
    <div className={`lens-case-datum${strong ? ' lens-case-datum-strong' : ''}${tone ? ` lens-case-datum-${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function classifyVault(value) {
  if (value < 250) return { label: '낮음', description: '250µm 미만으로 low vault 가능성이 큽니다.', tone: 'bad' };
  if (value < 350) return { label: '낮은 경계', description: '안전범위 안이지만 하한에 가까워 신중 확인이 필요합니다.', tone: 'caution' };
  if (value <= 650) {
    return Math.abs(value - 500) <= 80
      ? { label: '추천권', description: '목표 500µm에 가까운 우선 검토 후보입니다.', tone: 'best' }
      : { label: '적정', description: '안전범위 안이며 목표 범위에서 크게 벗어나지 않습니다.', tone: 'good' };
  }
  if (value <= 750) return { label: '높은 경계', description: '안전범위 안이지만 상한에 가까워 신중 확인이 필요합니다.', tone: 'caution' };
  return { label: '높음', description: '750µm 초과로 high vault 가능성이 큽니다.', tone: 'bad' };
}

function parseVaultValue(value) {
  const parsed = Number(String(value).replace(/[^\d.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

// 1일 vault, 1개월 vault, 목표값 같은 핵심 지표 카드
function Metric({ label, value, ok, status, tone }) {
  return (
    <div className="lens-metric">
      <span className="lens-metric-label">{label}</span>
      <strong className="lens-metric-value">{value}</strong>
      {status ? <span className={`lens-metric-status lens-status-${tone}`}>{status}</span> : null}
      {ok !== undefined ? <span className={`lens-metric-status ${ok ? 'lens-ok-text' : 'lens-bad-text'}`}>{ok ? '안전범위' : '범위 밖'}</span> : null}
    </div>
  );
}

export default LensRecommendFrame;
